import omit from 'lodash/omit';
import configs from 'configs';
import { authHeaders } from 'functions/authHeader';
import request from 'functions/api/request';

const apiUrl = configs.financeApiUrl;

// getAllPrograms regardless user profile; public
export const getFinancePrograms = async () => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/webapp/programs`,
    json: true,
  };

  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting programs', ex);
    throw ex;
  }

  return response?.programs;
};

// get all program categories; public
export const getFinanceCategories = async () => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/webapp/categories`,
    json: true,
  };

  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting profile data:', ex);
    throw ex;
  }

  return response?.categories;
};

export const getUserMatchedFinancePrograms = async (params) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/programs/match`,
    headers: await authHeaders(),
    data: omit(params, ['userWixId', 'email']),
    json: true,
  };

  try {
    const response = await request.post(requestOptions);
    return response.matchedPrograms;
  } catch (ex) {
    console.error('Error occurred, while getting finance match:', ex);
    throw ex;
  }
};

// get authenticated user finance profile
export const getUserFinanceProfile = async () => {
  const requestOptions = {
    url: `${apiUrl}/webapp/user/me`,
    headers: await authHeaders(),
    json: true,
  };

  // it is expected status 404 and so exception; it is ok case
  const { patient } = await request.get(requestOptions);

  return patient;
};

// update authenticated user finance profile
export const updateUserFinanceProfile = async (financeParams) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/user/me`,
    headers: await authHeaders(),
    data: financeParams,
    json: true,
  };

  try {
    return await request.put(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting profile data:', ex);
    throw ex;
  }
};

export const createUserFinanceProfile = async (params) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/user`,
    headers: await authHeaders(),
    data: params,
    json: true,
  };

  try {
    return await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, creating finance profile:', ex);
    throw ex;
  }
};

export const getMatchedFinanceProgramsPublic = async (params, { signal } = {}) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/programs/match/public`,
    data: {
      ...omit(params, ['userWixId', 'email']),
      stage: null,
    },
    json: true,
    signal,
  };

  try {
    const response = await request.post(requestOptions);
    return response.matchedPrograms;
  } catch (ex) {
    console.error('Error occurred, while getting public finance match:', ex);
    throw ex;
  }
};

export const updatePatientFinanceProfile = async ({ userWixId, data }) => {
  if (!userWixId) {
    throw Error('No wixId supplied to updateFinancePatient');
  }

  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants/patients/${userWixId}`,
    headers: await authHeaders(),
    data: omit(data, ['userWixId', 'email', 'location']),
    json: true,
  };

  try {
    return await request.put(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while updating patient finance profile:', ex);
    throw ex;
  }
};

export const createPatientFinanceProfile = async (patientFinanceData) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants/patients`,
    headers: await authHeaders(),
    data: patientFinanceData,
    json: true,
  };

  try {
    return await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while creating patient finance profile:', ex);
    throw ex;
  }
};

export const getPatientFinanceProfile = async (patientUserWixId) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants/patients/${patientUserWixId}`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    return await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting patient to existing user:', ex);
    throw ex;
  }
};

export const getCareAssistantPatients = async () => {
  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants/me`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    const response = await request.get(requestOptions);
    return response.patients;
  } catch (ex) {
    console.error('Error occurred, while getting patients', ex);
    throw ex;
  }
};

export const registerFinanceCaregiver = async (role) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants`,
    headers: await authHeaders(),
    data: {
      role,
    },
    json: true,
  };

  try {
    return await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while registering a caregiver:', ex);
    throw ex;
  }
};

export const getPatientMatchedFinancePrograms = async ({ userWixId, params }, { signal } = {}) => {
  const requestOptions = {
    url: `${apiUrl}/webapp/care-assistants/patients/${userWixId}/programs/match`,
    headers: await authHeaders(),
    data: params,
    json: true,
    signal,
  };

  try {
    const response = await request.post(requestOptions);
    return response.matchedPrograms;
  } catch (ex) {
    console.error('Error occurred, while getting public finance match:', ex);
    throw ex;
  }
};
